import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, switchMap, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { AccountsActions } from './account.actions';
import { AccountService } from '../../api-services/account/account.service';
import { AccountEquipmentService } from '../../api-services/account/account_equipment.service';
import { AccountClickService } from '../../api-services/account/account_click.service';
import { AppActions } from '../app/app.actions';
import { ClickActions } from '../click/click.actions';
import { Router } from '@angular/router';
import { AccountParameterService } from '../../api-services/account/account_parameter.service';
import { RootState } from '../store';
import { AccountScheduledActionsService } from '../../api-services/account/account_scheduled_actions.service';
import { AccountACMRemoteService } from '../../api-services/account/account_acm_remote.service';
import { PermissionService } from '../../local-services/permission.service';

@Injectable()
export class AccountsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<RootState>);
  private permissionService = inject(PermissionService);

  constructor(
    private router: Router,
    private accountService: AccountService,
    private accountEquipmentService: AccountEquipmentService,
    private accountClickService: AccountClickService,
    private accountParameterService: AccountParameterService,
    private accountScheduledActionsService: AccountScheduledActionsService,
    private accountACMRemoteService: AccountACMRemoteService
  ) { }

  loadAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadAccounts),
      concatMap((action) =>
        this.accountService.getAccounts(action.queryParams, action.page).pipe(
          switchMap((data) => [
            AccountsActions.loadAccountsSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadAccountById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadAccountById),
      concatMap((action) =>
        this.accountService.getAccountById(action.id).pipe(
          switchMap((data) => [
            AccountsActions.loadAccountByIdSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });
  navigateToAccountDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountsActions.loadAccountByIdSuccess),
        tap(() => {
          this.router.navigate(['main/accounts/details']);
        })
      ),
    { dispatch: false }
  );

  createAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.createAccount),
      withLatestFrom(
        this.store.select(state => ({
          accounts: state.accounts,
          pageSize: state.app?.pageSize ?? 10
        }))
      ),
      concatMap(([action, { accounts, pageSize }]) =>
        this.accountService.createAccount(action.payload).pipe(
          switchMap((data) => [
            AccountsActions.createAccountSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadAccounts({
              queryParams: {
                filters: { global: { value: accounts.filter } },
                rows: pageSize,
                first: pageSize * (accounts.pageIndex ?? 0),
                sort_field: accounts.sort?.sortField ?? 'id',
                sort_order: accounts.sort?.sortOrder ?? 1
              },
              page: accounts.pageIndex ?? 0
            })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  openCreatedAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.createAccountSuccess),
      concatMap(action => this.permissionService.isAllowed('enterAccountDetails').pipe(
        filter(isAllowed => isAllowed),
        map(() => AccountsActions.loadAccountById({ id: action.data.id }))
      ))
    );
  });

  deleteAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.deleteAccount),
      withLatestFrom(
        this.store.select(state => ({
          accounts: state.accounts,
          pageSize: state.app?.pageSize ?? 10
        }))
      ),
      concatMap(([action, { accounts, pageSize }]) =>
        this.accountService.deleteAccountById(action.accountId).pipe(
          switchMap(() => [
            AccountsActions.deleteAccountSuccess(),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadAccounts({
              queryParams: {
                filters: { global: { value: accounts.filter } },
                rows: pageSize,
                first: pageSize * (accounts.pageIndex ?? 0),
                sort_field: accounts.sort?.sortField ?? 'id',
                sort_order: accounts.sort?.sortOrder ?? 1
              },
              page: accounts.pageIndex ?? 0
            })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  unassignAccountFromDealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.unassignAccountFromDealer),
      concatMap(({ payload }) =>
        this.accountService.unassignAccountFromDealer(payload.id).pipe(
          switchMap((data) => [
            AccountsActions.unassignAccountFromDealerSuccess({ id: payload.id, data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadAvailibleEquipments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadAvailibleEquipments),
      concatMap((action) =>
        this.accountEquipmentService
          .getAvailableEquipments(action.dealerId)
          .pipe(
            switchMap((data) => [
              AccountsActions.loadAvailibleEquipmentsSuccess({ data }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  linkEquipmentToAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.linkEquipmentToAccount),
      concatMap((action) =>
        this.accountEquipmentService
          .linkEquipmentToAccount(action.accountId, action.equipmentId)
          .pipe(
            switchMap((data) => [
              AccountsActions.linkEquipmentToAccountSuccess({ data }),
              AppActions.setLoading({ loading: false }),
              AccountsActions.loadAccountById({ id: action.accountId })
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  removeEquipmentFromAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.removeEquipmentFromAccount),
      concatMap((action) =>
        this.accountEquipmentService
          .removeEquipmentFromAccount(action.accountId, action.equipmentId)
          .pipe(
            switchMap((data) => [
              AccountsActions.removeEquipmentFromAccountSuccess({ data }),
              AppActions.setLoading({ loading: false }),
              AccountsActions.loadAccountById({ id: action.accountId })
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  loadAccountLinkedDevices$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadAccountLinkedDevices),
      concatMap((action) =>
        this.accountClickService.getAccountDevices(action.accountId).pipe(
          switchMap((data) => [
            AccountsActions.loadAccountLinkedDevicesSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadLinkedDeviceByFirebaseId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadLinkedDeviceByFirebaseId),
      concatMap((action) =>
        this.accountClickService
          .getDeviceByFirebaseUid(action.accountId, action.firebaseUid)
          .pipe(
            switchMap((data) => [
              AccountsActions.loadLinkedDeviceByFirebaseIdSuccess({
                data,
                firebaseUid: action.firebaseUid,
              }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  removeDeviceFromAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.removeDeviceFromAccount),
      concatMap((action) =>
        this.accountClickService
          .removeDeviceFromAccount(action.accountId, action.firebaseUid)
          .pipe(
            switchMap(() => [
              AccountsActions.loadAccountLinkedDevices({ accountId: action.accountId, }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  searchDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.searchDevice),
      concatMap((action) =>
        this.accountClickService.searchDevice(action.mail).pipe(
          switchMap((data) => [
            AccountsActions.searchDeviceSuccess({ data }),
            ClickActions.loadTemplates(),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => [
            AccountsActions.searchDeviceFailure({ error }),
            AppActions.setLoading({ loading: false }),
          ]),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  linkDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.linkDevice),
      concatMap((action) =>
        this.accountClickService.addDevice(action.payload).pipe(
          switchMap((data) => [
            AccountsActions.linkDeviceSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.updateAccount),
      concatMap(({ payload }) =>
        this.accountService.updateAccount(payload).pipe(
          switchMap(() => [
            AccountsActions.updateAccountSuccess({ data: payload }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadAccountParameters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadAccountParameters),
      switchMap((action) =>
        this.accountParameterService.getAccountParameters(action.accountId).pipe(
          switchMap((data) => [
            AccountsActions.loadAccountParametersSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  createAccountParameter$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.createAccountParameter),
      switchMap((action) =>
        this.accountParameterService.createParameter(action.payload).pipe(
          switchMap((data) => [
            AccountsActions.createAccountParameterSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadAccountParameters({ accountId: action.payload.account_id }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateAccountParameter$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.updateAccountParameter),
      switchMap((action) =>
        this.accountParameterService.updateParameter(action.payload).pipe(
          switchMap((data) => [
            AccountsActions.updateAccountParameterSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadAccountParameters({ accountId: action.payload.account_id }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  deleteAccountParameter$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.deleteAccountParameter),
      switchMap((action) =>
        this.accountParameterService.deleteParameter(action.parameterId).pipe(
          switchMap((data) => [
            AccountsActions.deleteAccountParameterSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadAccountParameters({ accountId: action.accountId })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadScheduledActions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadScheduledActions),
      switchMap((action) =>
        this.accountScheduledActionsService.getScheduledActions(action.accountId).pipe(
          switchMap((data) => [
            AccountsActions.loadScheduledActionsSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadScheduledActionById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadScheduledActionById),
      switchMap((action) =>
        this.accountScheduledActionsService.getScheduledActionById(action.accountId, action.actionId).pipe(
          switchMap((data) => [
            AccountsActions.loadScheduledActionByIdSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  createScheduledAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.createScheduledAction),
      switchMap((action) =>
        this.accountScheduledActionsService.createScheduledAction(action.payload).pipe(
          switchMap((data) => [
            AccountsActions.createScheduledActionSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadScheduledActions({ accountId: action.payload.account_id })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateScheduledAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.updateScheduledAction),
      switchMap((action) =>
        this.accountScheduledActionsService.updateScheduledAction(action.payload).pipe(
          switchMap((data) => [
            AccountsActions.updateScheduledActionSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadScheduledActions({ accountId: action.payload.account_id })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  deleteScheduledAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.deleteScheduledAction),
      switchMap((action) =>
        this.accountScheduledActionsService.deleteScheduledAction(action.accountId, action.actionId).pipe(
          switchMap(() => [
            AccountsActions.deleteScheduledActionSuccess(),
            AppActions.setLoading({ loading: false }),
            AccountsActions.loadScheduledActions({ accountId: action.accountId })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadACMRemoteAssignmentsByAccountId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.loadACMRemoteAssignmentsByAccountId),
      concatMap((action) =>
        this.accountACMRemoteService.getACMRemoteAssignmentsByAccountId(action.accountId)
          .pipe(
            switchMap((data) => [
              AccountsActions.loadACMRemoteAssignmentsByAccountIdSuccess({ data }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  assignACMRemote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.assignACMRemote),
      concatMap((action) =>
        this.accountACMRemoteService.assignACMRemote(action.data)
          .pipe(
            switchMap((data) => [
              AccountsActions.assignACMRemoteSuccess({ data }),
              AccountsActions.loadACMRemoteAssignmentsByAccountId({ accountId: action.data.account_id }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  updateACMRemoteAssignment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.updateACMRemoteAssignment),
      concatMap((action) =>
        this.accountACMRemoteService.updateACMRemoteAssignment(action.data)
          .pipe(
            switchMap((data) => [
              AccountsActions.updateACMRemoteAssignmentSuccess({ data }),
              AccountsActions.loadACMRemoteAssignmentsByAccountId({ accountId: action.data.account_id }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  unassignACMRemote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountsActions.unassignACMRemote),
      concatMap((action) =>
        this.accountACMRemoteService.unassignACMRemote(action.assignmentId)
          .pipe(
            switchMap(() => [
              AccountsActions.unassignACMRemoteSuccess(),
              AccountsActions.loadACMRemoteAssignmentsByAccountId({ accountId: action.accountId }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

}
